import {
  CheckBoxOutlineBlankOutlined as UncheckedIcon,
  CheckBoxOutlined as CheckedIcon,
  Delete as DeleteIcon,
  MoreVert as MoreIcon,
  SettingsBackupRestore as ResetIcon,
} from "@mui/icons-material";
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton as MuiIconButton,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Rating,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import * as ids from "common/ids";
import * as numbers from "common/numbers";
import Component from "components/Component";
import IconButton from "components/common/IconButton";
import Notes from "components/common/Notes";
import PrettyNumberTextField from "components/common/PrettyNumberTextField";
import * as labels from "config.labels";
import { useRef, useState } from "react";
import { connect, useDispatch } from "react-redux";
import * as staticDataService from "services/staticDataService";
import * as flagActions from "store/actions/input/flags/flagsActions";
import * as staticDataHooks from "store/hooks/staticDataHooks";
import * as flagSelectors from "store/selectors/input/flags/flagsSelectors";

const LABEL_VARIANT = "body2";
const LABEL_INDENT_WIDTH = 4;

const makeTypeLabel = (flag) => {
  return flag?.displayName ?? flag?.type ?? "";
};

const TextFlagCells = ({ flag, updateType, updateCategory }) => {
  const isTypeEditable = typeof updateType === "function" && !flag?.isDefault;
  const isCategoryEditable = typeof updateCategory === "function";
  const typeLabel = makeTypeLabel(flag);

  return (
    <>
      <TableCell
        sx={{ verticalAlign: "center", textAlign: "left" }}
        className={"cell-condensed-padding-md"}
      >
        <Tooltip title={typeLabel}>
          {!isTypeEditable ? (
            <Typography
              variant={LABEL_VARIANT}
              sx={{
                paddingLeft: (flag?.indent ?? 0) * LABEL_INDENT_WIDTH,
              }}
            >
              {typeLabel}
            </Typography>
          ) : (
            <TextField
              value={flag?.type}
              onChange={(e) => updateType(e.target.value)}
              size={"small"}
              variant={"filled"}
              autoComplete={"off"}
              hiddenLabel
              fullWidth
            />
          )}
        </Tooltip>
      </TableCell>
      <TableCell
        sx={{ verticalAlign: "center", textAlign: "left" }}
        className={"cell-condensed-padding-md"}
      >
        {!isCategoryEditable ? (
          <Typography variant={LABEL_VARIANT}>
            {flag?.category ?? ""}
          </Typography>
        ) : (
          <TextField
            value={flag?.category ?? ""}
            onChange={(e) => updateCategory(e.target.value)}
            size={"small"}
            variant={"filled"}
            autoComplete={"off"}
            hiddenLabel
            fullWidth
          />
        )}
      </TableCell>
    </>
  );
};

const NoneFlagCells = ({ flag, updateType }) => {
  const isTypeEditable = typeof updateType === "function" && !flag?.isDefault;
  const typeLabel = makeTypeLabel(flag);

  return (
    <TableCell
      sx={{ verticalAlign: "center", textAlign: "left" }}
      className={"cell-condensed-padding-md"}
      colSpan={2}
    >
      <Tooltip title={typeLabel}>
        {!isTypeEditable ? (
          <Typography
            variant={LABEL_VARIANT}
            sx={{
              paddingLeft: (flag?.indent ?? 0) * LABEL_INDENT_WIDTH,
            }}
          >
            {typeLabel}
          </Typography>
        ) : (
          <TextField
            value={flag?.type}
            onChange={(e) => updateType(e.target.value)}
            size={"small"}
            variant={"filled"}
            autoComplete={"off"}
            hiddenLabel
            fullWidth
          />
        )}
      </Tooltip>
    </TableCell>
  );
};

const BooleanFlagCells = ({ flag, updateCategory }) => {
  const isCategoryEditable = typeof updateCategory === "function";
  const typeLabel = makeTypeLabel(flag);

  if (!isCategoryEditable) {
    return (
      <>
        <TableCell
          sx={{ verticalAlign: "center", textAlign: "left" }}
          className={"cell-condensed-padding-md"}
        >
          <Typography
            variant={LABEL_VARIANT}
            sx={{
              paddingLeft: (flag?.indent ?? 0) * LABEL_INDENT_WIDTH,
            }}
          >
            {typeLabel}
          </Typography>
        </TableCell>
        <TableCell
          sx={{ verticalAlign: "center", textAlign: "left" }}
          className={"cell-condensed-padding-md"}
        >
          {flag?.category ? (
            <CheckedIcon fontSize={"small"} />
          ) : (
            <UncheckedIcon fontSize={"small"} />
          )}
        </TableCell>
      </>
    );
  }

  const checked = !!flag?.category;

  return (
    <TableCell
      sx={{ verticalAlign: "center", textAlign: "left" }}
      className={"cell-condensed-padding-md"}
      colSpan={2}
    >
      <Tooltip title={typeLabel}>
        <Stack
          direction={"row"}
          spacing={1}
          alignItems={"center"}
          justifyContent={"space-between"}
          onClick={() => updateCategory(!checked)}
          sx={{ cursor: "pointer" }}
        >
          <Typography
            variant={LABEL_VARIANT}
            sx={{
              paddingLeft: (flag?.indent ?? 0) * LABEL_INDENT_WIDTH,
            }}
          >
            {typeLabel}
          </Typography>
          <Checkbox
            size={"small"}
            checked={checked}
            disabled={!isCategoryEditable}
          />
        </Stack>
      </Tooltip>
    </TableCell>
  );
};

const SelectFlagCells = ({ flag, updateCategory }) => {
  const isCategoryEditable = typeof updateCategory === "function";
  const typeLabel = makeTypeLabel(flag);

  const NULL_KEY = "__NULL__";

  if (!isCategoryEditable) {
    const category =
      flag?.categoryOptions?.filter(({ key }) => key === flag?.category)?.[0]
        ?.display ?? "";
    return (
      <>
        <TableCell
          sx={{ verticalAlign: "center", textAlign: "left" }}
          className={"cell-condensed-padding-md"}
        >
          <Typography
            variant={LABEL_VARIANT}
            sx={{
              paddingLeft: (flag?.indent ?? 0) * LABEL_INDENT_WIDTH,
            }}
          >
            {typeLabel}
          </Typography>
        </TableCell>
        <TableCell
          sx={{ verticalAlign: "center", textAlign: "left" }}
          className={"cell-condensed-padding-md"}
        >
          <Typography variant={LABEL_VARIANT}>{category}</Typography>
        </TableCell>
      </>
    );
  }

  return (
    <TableCell
      sx={{ verticalAlign: "center", textAlign: "left" }}
      className={"cell-condensed-padding-md"}
      colSpan={2}
    >
      <Tooltip title={typeLabel}>
        <FormControl sx={{ width: "100%" }}>
          <InputLabel shrink>{typeLabel}</InputLabel>
          <Select
            label={typeLabel}
            notched
            variant={"outlined"}
            size={"small"}
            value={flag?.category ?? NULL_KEY}
            onChange={(e) => updateCategory(e.target.value)}
            disabled={!isCategoryEditable}
          >
            {flag?.nullCategoryOption && (
              <MenuItem value={NULL_KEY}>{flag?.nullCategoryOption}</MenuItem>
            )}
            {flag?.categoryOptions?.map(({ key, display }) => (
              <MenuItem key={key} value={key}>
                {display}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Tooltip>
    </TableCell>
  );
};

const SwitchFlagCells = ({ flag, updateCategory }) => {
  const isCategoryEditable = typeof updateCategory === "function";
  const typeLabel = makeTypeLabel(flag);

  if (!isCategoryEditable) {
    return (
      <>
        <TableCell
          sx={{ verticalAlign: "center", textAlign: "left" }}
          className={"cell-condensed-padding-md"}
        >
          <Typography
            variant={LABEL_VARIANT}
            sx={{
              paddingLeft: (flag?.indent ?? 0) * LABEL_INDENT_WIDTH,
            }}
          >
            {typeLabel}
          </Typography>
        </TableCell>
        <TableCell
          sx={{ verticalAlign: "center", textAlign: "left" }}
          className={"cell-condensed-padding-md"}
        >
          <Switch size={"small"} checked={!!flag?.category} disabled />
        </TableCell>
      </>
    );
  }

  const checked = !!flag?.category;

  return (
    <TableCell
      sx={{ verticalAlign: "center", textAlign: "left" }}
      className={"cell-condensed-padding-md"}
      colSpan={2}
    >
      <Tooltip title={typeLabel}>
        <FormControlLabel
          control={
            <Switch
              size={"small"}
              checked={checked}
              onChange={(e) => updateCategory(e.target.checked)}
              disabled={!isCategoryEditable}
            />
          }
          label={
            <Typography
              variant={LABEL_VARIANT}
              sx={{
                paddingLeft: (flag?.indent ?? 0) * LABEL_INDENT_WIDTH,
              }}
            >
              {typeLabel}
            </Typography>
          }
          labelPlacement={"start"}
          sx={{ margin: 0, width: "100%", justifyContent: "space-between" }}
        />
      </Tooltip>
    </TableCell>
  );
};

const NumberFlagCells = ({ flag, updateCategory }) => {
  const isCategoryEditable = typeof updateCategory === "function";
  const typeLabel = makeTypeLabel(flag);

  if (!isCategoryEditable) {
    return (
      <>
        <TableCell
          sx={{ verticalAlign: "center", textAlign: "left" }}
          className={"cell-condensed-padding-md"}
        >
          <Typography
            variant={LABEL_VARIANT}
            sx={{
              paddingLeft: (flag?.indent ?? 0) * LABEL_INDENT_WIDTH,
            }}
          >
            {typeLabel}
          </Typography>
        </TableCell>
        <TableCell
          sx={{ verticalAlign: "center", textAlign: "left" }}
          className={"cell-condensed-padding-md"}
        >
          <Typography variant={LABEL_VARIANT}>
            {flag?.category !== null && flag?.category !== undefined
              ? Number(flag.category).toLocaleString()
              : ""}
          </Typography>
        </TableCell>
      </>
    );
  }

  return (
    <TableCell
      sx={{ verticalAlign: "center", textAlign: "left" }}
      className={"cell-condensed-padding-md"}
      colSpan={2}
    >
      <Tooltip title={typeLabel}>
        <FormControl sx={{ width: "100%" }}>
          <PrettyNumberTextField
            label={typeLabel}
            size={"small"}
            value={
              flag?.category !== null && flag?.category !== undefined
                ? Number(flag.category)
                : null
            }
            onChangeNumber={(value) => updateCategory(value)}
            disabled={!isCategoryEditable}
            variant={"filled"}
            fullWidth
          />
        </FormControl>
      </Tooltip>
    </TableCell>
  );
};

const IntFlagCells = ({ flag, updateCategory }) => {
  const isCategoryEditable = typeof updateCategory === "function";
  const typeLabel = makeTypeLabel(flag);

  if (!isCategoryEditable) {
    return (
      <>
        <TableCell
          sx={{ verticalAlign: "center", textAlign: "left" }}
          className={"cell-condensed-padding-md"}
        >
          <Typography
            variant={LABEL_VARIANT}
            sx={{
              paddingLeft: (flag?.indent ?? 0) * LABEL_INDENT_WIDTH,
            }}
          >
            {typeLabel}
          </Typography>
        </TableCell>
        <TableCell
          sx={{ verticalAlign: "center", textAlign: "left" }}
          className={"cell-condensed-padding-md"}
        >
          <Typography variant={LABEL_VARIANT}>
            {flag?.category !== null && flag?.category !== undefined
              ? numbers.dpString(0)(flag.category)
              : ""}
          </Typography>
        </TableCell>
      </>
    );
  }

  return (
    <TableCell
      sx={{ verticalAlign: "center", textAlign: "left" }}
      className={"cell-condensed-padding-md"}
      colSpan={2}
    >
      <Tooltip title={typeLabel}>
        <FormControl sx={{ width: "100%" }}>
          <PrettyNumberTextField
            label={typeLabel}
            size={"small"}
            value={
              flag?.category !== null && flag?.category !== undefined
                ? Number(flag.category)
                : null
            }
            onChangeNumber={(value) => {
              const roundedValue = value !== null ? Math.round(value) : null;
              updateCategory(roundedValue);
            }}
            disabled={!isCategoryEditable}
            variant={"filled"}
            fullWidth
          />
        </FormControl>
      </Tooltip>
    </TableCell>
  );
};

const CurrencyFlagCells = ({ flag, updateCategory }) => {
  const isCategoryEditable = typeof updateCategory === "function";
  const typeLabel = makeTypeLabel(flag);

  if (!isCategoryEditable) {
    return (
      <>
        <TableCell
          sx={{ verticalAlign: "center", textAlign: "left" }}
          className={"cell-condensed-padding-md"}
        >
          <Typography
            variant={LABEL_VARIANT}
            sx={{
              paddingLeft: (flag?.indent ?? 0) * LABEL_INDENT_WIDTH,
            }}
          >
            {typeLabel}
          </Typography>
        </TableCell>
        <TableCell
          sx={{ verticalAlign: "center", textAlign: "left" }}
          className={"cell-condensed-padding-md"}
        >
          <Typography variant={LABEL_VARIANT}>
            {flag?.category !== null && flag?.category !== undefined
              ? `$${Number(flag.category).toLocaleString()}`
              : ""}
          </Typography>
        </TableCell>
      </>
    );
  }

  return (
    <TableCell
      sx={{ verticalAlign: "center", textAlign: "left" }}
      className={"cell-condensed-padding-md"}
      colSpan={2}
    >
      <Tooltip title={typeLabel}>
        <FormControl sx={{ width: "100%" }}>
          <PrettyNumberTextField
            label={typeLabel}
            size={"small"}
            value={
              flag?.category !== null && flag?.category !== undefined
                ? Number(flag.category)
                : null
            }
            onChangeNumber={(value) => updateCategory(value)}
            disabled={!isCategoryEditable}
            prefix={"$"}
            variant={"filled"}
            fullWidth
          />
        </FormControl>
      </Tooltip>
    </TableCell>
  );
};

const PercentageFlagCells = ({ flag, updateCategory }) => {
  const isCategoryEditable = typeof updateCategory === "function";
  const typeLabel = makeTypeLabel(flag);

  if (!isCategoryEditable) {
    return (
      <>
        <TableCell
          sx={{ verticalAlign: "center", textAlign: "left" }}
          className={"cell-condensed-padding-md"}
        >
          <Typography
            variant={LABEL_VARIANT}
            sx={{
              paddingLeft: (flag?.indent ?? 0) * LABEL_INDENT_WIDTH,
            }}
          >
            {typeLabel}
          </Typography>
        </TableCell>
        <TableCell
          sx={{ verticalAlign: "center", textAlign: "left" }}
          className={"cell-condensed-padding-md"}
        >
          <Typography variant={LABEL_VARIANT}>
            {flag?.category !== null && flag?.category !== undefined
              ? `${Number(flag.category).toLocaleString()}%`
              : ""}
          </Typography>
        </TableCell>
      </>
    );
  }

  return (
    <TableCell
      sx={{ verticalAlign: "center", textAlign: "left" }}
      className={"cell-condensed-padding-md"}
      colSpan={2}
    >
      <Tooltip title={typeLabel}>
        <FormControl sx={{ width: "100%" }}>
          <PrettyNumberTextField
            label={typeLabel}
            size={"small"}
            value={
              flag?.category !== null && flag?.category !== undefined
                ? Number(flag.category)
                : null
            }
            onChangeNumber={(value) => updateCategory(value)}
            disabled={!isCategoryEditable}
            suffix={"%"}
            variant={"filled"}
            fullWidth
          />
        </FormControl>
      </Tooltip>
    </TableCell>
  );
};

const RatingFlagCells = ({ flag, updateCategory }) => {
  const isCategoryEditable = typeof updateCategory === "function";
  const typeLabel = makeTypeLabel(flag);

  const precision =
    flag?.categoryOptions?.find((opt) => opt.key === "precision")?.display || 1;
  const max =
    flag?.categoryOptions?.find((opt) => opt.key === "max")?.display || 5;

  if (!isCategoryEditable) {
    return (
      <>
        <TableCell
          sx={{ verticalAlign: "center", textAlign: "left" }}
          className={"cell-condensed-padding-md"}
        >
          <Typography
            variant={LABEL_VARIANT}
            sx={{
              paddingLeft: (flag?.indent ?? 0) * LABEL_INDENT_WIDTH,
            }}
          >
            {typeLabel}
          </Typography>
        </TableCell>
        <TableCell
          sx={{ verticalAlign: "center", textAlign: "left" }}
          className={"cell-condensed-padding-md"}
        >
          <Rating
            value={
              flag?.category !== null && flag?.category !== undefined
                ? Number(flag.category)
                : 0
            }
            precision={Number(precision)}
            max={Number(max)}
            readOnly
          />
        </TableCell>
      </>
    );
  }

  return (
    <TableCell
      sx={{ verticalAlign: "center", textAlign: "left" }}
      className={"cell-condensed-padding-md"}
      colSpan={2}
    >
      <Tooltip title={typeLabel}>
        <Stack
          direction={"row"}
          spacing={1}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          <Typography
            variant={LABEL_VARIANT}
            sx={{
              paddingLeft: (flag?.indent ?? 0) * LABEL_INDENT_WIDTH,
            }}
          >
            {typeLabel}
          </Typography>
          <Rating
            value={
              flag?.category !== null && flag?.category !== undefined
                ? Number(flag.category)
                : 0
            }
            onChange={(event, newValue) => updateCategory(newValue)}
            precision={Number(precision)}
            max={Number(max)}
            disabled={!isCategoryEditable}
          />
        </Stack>
      </Tooltip>
    </TableCell>
  );
};

export const ExposureFlagCells = ({ flag, updateType, updateCategory }) => {
  let FlagCells;

  if (flag?.categoryType === "none") {
    FlagCells = NoneFlagCells;
  } else if (flag?.categoryType === "boolean" || flag?.isBooleanFlag) {
    FlagCells = BooleanFlagCells;
  } else if (flag?.categoryType === "switch") {
    FlagCells = SwitchFlagCells;
  } else if (flag?.categoryType === "select") {
    FlagCells = SelectFlagCells;
  } else if (flag?.categoryType === "number") {
    FlagCells = NumberFlagCells;
  } else if (flag?.categoryType === "int") {
    FlagCells = IntFlagCells;
  } else if (flag?.categoryType === "currency") {
    FlagCells = CurrencyFlagCells;
  } else if (flag?.categoryType === "percentage") {
    FlagCells = PercentageFlagCells;
  } else if (flag?.categoryType === "rating") {
    FlagCells = RatingFlagCells;
  } else {
    FlagCells = TextFlagCells;
  }

  return (
    <FlagCells
      flag={flag}
      updateType={updateType}
      updateCategory={updateCategory}
    />
  );
};

const ExposureFlag = ({
  flag,
  index,
  deleteExposure,
  updateType,
  updateCategory,
  updateComment,
  showComments = true,
}) => {
  return (
    <TableRow
      sx={flag?.rowSx}
      className={
        flag?.categoryType === "none" ? "flag-section-header" : undefined
      }
    >
      <TableCell>
        {!flag?.isDefault && flag?.canDelete ? (
          <IconButton
            icon={DeleteIcon}
            onClick={() => deleteExposure(flag.id)}
          />
        ) : (
          <></>
        )}
      </TableCell>
      <ExposureFlagCells
        flag={flag}
        updateType={updateType}
        updateCategory={updateCategory}
      />
      {showComments && (
        <TableCell
          style={{ verticalAlign: "top" }}
          className={"cell-condensed-padding-md"}
        >
          {!flag?.noComments && (
            <Notes
              title={null}
              hasEditButton={false}
              update={(comment) => updateComment(comment)}
              value={flag?.comments}
            />
          )}
        </TableCell>
      )}
    </TableRow>
  );
};

// Buckets rows into the config-defined columns of sections; unassigned rows stay in rest
const splitByLayout = (flags, columns) => {
  const headerTypes = new Set(columns.flat());
  const sections = {};
  const rest = [];
  let current = null;
  for (const flag of flags ?? []) {
    if (headerTypes.has(flag?.type)) {
      current = flag.type;
      sections[current] = [flag];
    } else if (
      flag?.categoryType === "none" ||
      (!flag?.isDefault && !flag?.canDelete)
    ) {
      current = null;
      rest.push(flag);
    } else if (current != null) {
      sections[current].push(flag);
    } else {
      rest.push(flag);
    }
  }
  const columnRows = columns.map((headers) =>
    headers.flatMap((header) => sections[header] ?? [])
  );
  return { columnRows, rest };
};

const ActionMenu = () => {
  const translateLabel = staticDataHooks.useTranslator("uiLabels");
  const dispatch = useDispatch();

  const [queryConfig] = staticDataService.useLazyConfigQuery("flags");

  const anchorRef = useRef(null);

  const [isOpen, setIsOpen] = useState(false);

  const resetExposureFlags = async () => {
    const { data } = await queryConfig("flags");
    const exposures = (data?.defaultExposureFlags ?? []).map((flag) => ({
      ...flag,
      id: ids.makeNewId(),
    }));
    dispatch(
      flagActions.update({
        exposures: exposures,
      })
    );
    setIsOpen(false);
  };

  return (
    <div className={"menu"}>
      <MuiIconButton
        ref={anchorRef}
        onClick={() => setIsOpen(!isOpen)}
        variant={"text"}
        className={"toggle"}
        style={{ marginLeft: "-15px" }}
      >
        <MoreIcon />
      </MuiIconButton>

      <Menu
        open={isOpen}
        anchorEl={anchorRef.current}
        onClose={(_, reason) => {
          if (reason === "backdropClick") setIsOpen(!isOpen);
        }}
      >
        <MenuItem onClick={resetExposureFlags}>
          {" "}
          <ListItemIcon>
            <ResetIcon />
          </ListItemIcon>
          <ListItemText>
            {translateLabel(labels.EXPOSURE_FLAGS_RESET_TO_LATEST, {
              default: "Reset to the latest flags",
            })}
          </ListItemText>
        </MenuItem>
      </Menu>
    </div>
  );
};

const FlagsTable = ({
  rows,
  showComments,
  columnLayout,
  handleExposureChange,
  deleteExposureFlag,
}) => {
  const translateLabel = staticDataHooks.useTranslator("uiLabels");
  return (
    <TableContainer>
      <Table
        size={"small"}
        className={"flags"}
        sx={
          columnLayout
            ? { "& .flag-section-header + tr td": { pt: 1.5 } }
            : undefined
        }
      >
        <TableHead>
          <TableRow>
            <TableCell width={"5%"} sx={{ minWidth: "30px" }} />
            <TableCell width={"25%"}>
              {translateLabel(labels.EXPOSURE_FLAG_TYPE_COLUMN, {
                default: "Type",
              })}
            </TableCell>
            <TableCell width={"20%"} align={"center"}>
              {!columnLayout &&
                translateLabel(labels.EXPOSURE_FLAG_CATEGORY_COLUMN, {
                  default: "Category",
                })}
            </TableCell>
            {showComments && (
              <TableCell width={"50%"}>
                {translateLabel(labels.EXPOSURE_FLAG_COMMENTS_COLUMN, {
                  default: "Comments",
                })}
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows?.map((flag, index) => (
            <ExposureFlag
              key={index}
              flag={flag}
              showComments={showComments}
              deleteExposure={() => deleteExposureFlag(flag.id)}
              updateType={(type) => handleExposureChange(flag, "type", type)}
              updateCategory={(category) =>
                handleExposureChange(flag, "category", category)
              }
              updateComment={(comment) =>
                handleExposureChange(flag, "comments", comment)
              }
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export const ExposureFlagsComponent = ({
  flags,
  deleteExposureFlag,
  updateExposureFlag,
  createExposure,
}) => {
  const translateLabel = staticDataHooks.useTranslator("uiLabels");
  const { data: flagsConfig } = staticDataService.useConfigQuery("flags");
  const layoutColumns = flagsConfig?.exposureFlagsLayout?.columns;
  // Hide the whole Comments column when every configured row opts out via noComments
  const realFlags =
    flags?.filter((flag) => flag?.isDefault || flag?.canDelete) ?? [];
  const showComments =
    realFlags.length === 0 || realFlags.some((flag) => !flag?.noComments);
  const newRowId = flags?.[flags.length - 1]?.id;
  const handleExposureChange = (row, column, value) => {
    if (row.id === newRowId) {
      const newExposure = { ...row, [column]: value };
      createExposure(newExposure);
    } else {
      updateExposureFlag(row.id, column, value);
    }
  };

  const layout = layoutColumns?.length
    ? splitByLayout(flags, layoutColumns)
    : null;
  const tableProps = {
    showComments,
    columnLayout: layout != null,
    handleExposureChange,
    deleteExposureFlag,
  };

  return (
    <Component
      title={translateLabel(labels.EXPOSURE_FLAGS_TITLE, {
        default: "Exposure Flags",
      })}
      subtitle={translateLabel(labels.EXPOSURE_FLAGS_SUBTITLE, {
        default:
          "Important exposure details that factors into risk assessment. These inputs do not factor into pricing calculations.",
      })}
      options={<ActionMenu />}
    >
      {layout == null ? (
        <FlagsTable rows={flags} {...tableProps} />
      ) : (
        <Stack spacing={2}>
          <Grid container spacing={2} alignItems={"flex-start"}>
            {layout.columnRows.map((rows, column) => (
              <Grid item xs={12} md={12 / layoutColumns.length} key={column}>
                <FlagsTable rows={rows} {...tableProps} />
              </Grid>
            ))}
          </Grid>
          {layout.rest.length > 0 && (
            <FlagsTable rows={layout.rest} {...tableProps} />
          )}
        </Stack>
      )}
    </Component>
  );
};

const mapDispatchToProps = {
  updateExposureFlag: flagActions.updateExposureFlag,
  deleteExposureFlag: flagActions.deleteExposure,
  createExposure: flagActions.createExposure,
};

const mapStateToProps = (state) => {
  return {
    flags: flagSelectors.getExposuresForDisplay(state),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ExposureFlagsComponent);
